import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Ban, BarChart3, MessageSquareWarning, Plus, Trash2, Loader2, X, Check, Eye, Mail, FileBarChart, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { blacklistAPI, surveyAPI, chatAPI, reportsAPI } from '../../services/api';

export function CommunityToolsPanel() {
  return (
    <Card className="shadow-lg border-0 mt-6">
      <CardHeader className="bg-gk-navy text-white rounded-t-xl">
        <CardTitle className="flex items-center gap-2 text-lg text-white">Community Tools</CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <Tabs defaultValue="blacklist" className="space-y-4">
          <TabsList className="bg-slate-100 p-1 rounded-lg">
            <TabsTrigger value="blacklist" data-testid="mgmt-blacklist-tab" className="data-[state=active]:bg-red-600 data-[state=active]:text-white"><Ban className="w-3.5 h-3.5 mr-1" />Blacklist</TabsTrigger>
            <TabsTrigger value="surveys" data-testid="mgmt-surveys-tab" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white"><BarChart3 className="w-3.5 h-3.5 mr-1" />Surveys</TabsTrigger>
            <TabsTrigger value="reports" data-testid="mgmt-chat-reports-tab" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white"><MessageSquareWarning className="w-3.5 h-3.5 mr-1" />Chat Reports</TabsTrigger>
            <TabsTrigger value="digest" data-testid="mgmt-digest-tab" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white"><FileBarChart className="w-3.5 h-3.5 mr-1" />Monthly Digest</TabsTrigger>
          </TabsList>
          <TabsContent value="blacklist"><BlacklistAdmin /></TabsContent>
          <TabsContent value="surveys"><SurveysAdmin /></TabsContent>
          <TabsContent value="reports"><ChatReportsAdmin /></TabsContent>
          <TabsContent value="digest"><MonthlyDigestAdmin /></TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

// --- Blacklist ---
function BlacklistAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ plate_number: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await blacklistAPI.list(); setItems(data || []); }
    catch { toast.error('Failed to load blacklist'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.plate_number.trim()) return toast.error('Plate is required');
    setSubmitting(true);
    try { await blacklistAPI.add({ plate_number: form.plate_number.trim().toUpperCase(), reason: form.reason }); toast.success('Added to blacklist'); setForm({ plate_number: '', reason: '' }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
    finally { setSubmitting(false); }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Remove ${entry.plate_number} from blacklist?`)) return;
    try { await blacklistAPI.remove(entry.id, entry.source); toast.success('Removed'); load(); }
    catch { toast.error('Failed to remove'); }
  };

  return (
    <div className="space-y-3">
      <div className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-2">
        <p className="text-xs font-semibold text-red-700">Add a plate to the blacklist</p>
        <div className="flex gap-2">
          <Input data-testid="blacklist-plate-input" placeholder="e.g., KA01ZZ9999" value={form.plate_number} onChange={(e) => setForm({ ...form, plate_number: e.target.value.toUpperCase() })} className="font-mono" />
          <Input data-testid="blacklist-reason-input" placeholder="Reason (e.g., harassment, theft)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          <Button data-testid="blacklist-add-button" onClick={add} disabled={submitting} className="bg-red-600 hover:bg-red-700 text-white">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
      </div>
      {loading ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-red-500" /></div> : items.length === 0 ? <p className="text-center py-6 text-sm text-slate-500">No blacklisted vehicles yet.</p> : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={`${it.source}-${it.id}`} data-testid={`blacklist-row-${it.id}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="min-w-0 flex-1">
                <div className="font-mono font-bold text-sm flex items-center gap-1">
                  <span>{it.plate_number}</span>
                  <Badge variant="outline" className="text-[10px]">{it.source}</Badge>
                </div>
                {it.owner_name && <p className="text-xs text-slate-600">{it.owner_name} · Villa {it.villa_number}</p>}
                {it.reason && <p className="text-xs text-red-600 mt-0.5">Reason: {it.reason}</p>}
              </div>
              <Button data-testid={`blacklist-remove-${it.id}`} size="sm" variant="ghost" onClick={() => remove(it)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Surveys ---
function SurveysAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showResults, setShowResults] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await surveyAPI.listForManagement(); setItems(data || []); }
    catch { toast.error('Failed to load surveys'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeSurvey = async (id) => {
    if (!window.confirm('Close this survey to new responses?')) return;
    try { await surveyAPI.close(id); toast.success('Closed'); load(); } catch { toast.error('Failed'); }
  };
  const removeSurvey = async (id) => {
    if (!window.confirm('Delete this survey and all responses?')) return;
    try { await surveyAPI.remove(id); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };

  const updateExpiry = async (s) => {
    // Pre-fill with current expiry in local datetime format (yyyy-MM-ddTHH:mm)
    const cur = s.expires_at ? new Date(s.expires_at) : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, '0');
    const localDefault = `${cur.getFullYear()}-${pad(cur.getMonth() + 1)}-${pad(cur.getDate())}T${pad(cur.getHours())}:${pad(cur.getMinutes())}`;
    const input = window.prompt('Set new expiry (YYYY-MM-DD HH:MM, your local time):', localDefault.replace('T', ' '));
    if (!input) return;
    const dt = new Date(input.replace(' ', 'T'));
    if (isNaN(dt.getTime())) return toast.error('Invalid date — use format YYYY-MM-DD HH:MM');
    if (dt.getTime() <= Date.now()) return toast.error('Expiry must be in the future');
    try {
      await surveyAPI.updateExpiry(s.id, dt.toISOString());
      toast.success(`Expiry updated · closes ${dt.toLocaleString()}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update expiry'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-xs text-slate-600">{items.length} total · {items.filter(s => s.status === 'active').length} active</p>
        <Button data-testid="create-survey-button" onClick={() => setShowCreate(true)} size="sm" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="w-4 h-4 mr-1" />New Poll/Survey</Button>
      </div>
      {loading ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div> : items.length === 0 ? <p className="text-center py-6 text-sm text-slate-500">No surveys yet — post one to get neighbor feedback.</p> : (
        <div className="space-y-2">
          {items.map((s) => (
            <div key={s.id} data-testid={`survey-row-${s.id}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-sm truncate">{s.title}</p>
                  <Badge variant="outline" className="text-[10px] capitalize">{s.type}</Badge>
                  <Badge className={`text-[10px] ${s.status === 'active' ? 'bg-emerald-500' : 'bg-slate-400'}`}>{s.status}</Badge>
                  {s.anonymous && <Badge variant="outline" className="text-[10px]">anonymous</Badge>}
                </div>
                <p className="text-xs text-slate-500">{s.questions.length} question(s) · {s.response_count} response(s){s.expires_at && ` · closes ${new Date(s.expires_at).toLocaleString()}`}</p>
              </div>
              <div className="flex gap-1">
                <Button data-testid={`survey-results-${s.id}`} size="sm" variant="ghost" onClick={() => setShowResults(s)} className="text-indigo-600"><Eye className="w-4 h-4" /></Button>
                {s.status === 'active' && <Button data-testid={`survey-expiry-${s.id}`} size="sm" variant="ghost" onClick={() => updateExpiry(s)} className="text-cyan-600" title="Edit expiry"><Calendar className="w-4 h-4" /></Button>}
                {s.status === 'active' && <Button data-testid={`survey-close-${s.id}`} size="sm" variant="ghost" onClick={() => closeSurvey(s.id)} className="text-amber-600" title="Stop receiving responses"><X className="w-4 h-4" /></Button>}
                <Button data-testid={`survey-delete-${s.id}`} size="sm" variant="ghost" onClick={() => removeSurvey(s.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <SurveyCreateDialog open={showCreate} onOpenChange={setShowCreate} onCreated={load} />
      {showResults && <SurveyResultsDialog survey={showResults} onClose={() => setShowResults(null)} />}
    </div>
  );
}

function SurveyCreateDialog({ open, onOpenChange, onCreated }) {
  const [type, setType] = useState('poll');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [anonymous, setAnonymous] = useState(true);
  const [validDays, setValidDays] = useState(7);
  const [expiryMode, setExpiryMode] = useState('days'); // 'days' | 'date'
  const [expiresAt, setExpiresAt] = useState(''); // local datetime string
  const [questions, setQuestions] = useState([{ question: '', options: ['', ''] }]);
  const [submitting, setSubmitting] = useState(false);

  const addQ = () => questions.length < 20 && setQuestions([...questions, { question: '', options: ['', ''] }]);
  const removeQ = (idx) => setQuestions(questions.filter((_, i) => i !== idx));
  const updateQ = (idx, key, val) => setQuestions(questions.map((q, i) => i === idx ? { ...q, [key]: val } : q));
  const addOpt = (qIdx) => { const q = questions[qIdx]; if (q.options.length < 10) updateQ(qIdx, 'options', [...q.options, '']); };
  const removeOpt = (qIdx, oIdx) => { const q = questions[qIdx]; if (q.options.length > 2) updateQ(qIdx, 'options', q.options.filter((_, i) => i !== oIdx)); };
  const updateOpt = (qIdx, oIdx, val) => { const q = questions[qIdx]; updateQ(qIdx, 'options', q.options.map((o, i) => i === oIdx ? val : o)); };

  const submit = async () => {
    if (!title.trim()) return toast.error('Title is required');
    if (type === 'poll' && questions.length !== 1) return toast.error('Polls have exactly 1 question');
    for (const q of questions) {
      if (!q.question.trim()) return toast.error('Each question needs text');
      if (q.options.filter(o => o.trim()).length < 2) return toast.error('Each question needs at least 2 options');
    }
    let expiryPayload = {};
    if (expiryMode === 'date') {
      if (!expiresAt) return toast.error('Pick an expiry date & time');
      const dt = new Date(expiresAt);
      if (isNaN(dt.getTime()) || dt.getTime() <= Date.now()) return toast.error('Expiry must be in the future');
      expiryPayload = { expires_at: dt.toISOString() };
    } else {
      expiryPayload = { valid_days: parseInt(validDays, 10) };
    }
    setSubmitting(true);
    try {
      await surveyAPI.create({ title: title.trim(), description: description.trim(), type, anonymous, ...expiryPayload, questions: questions.map(q => ({ question: q.question.trim(), options: q.options.map(o => o.trim()).filter(Boolean) })) });
      toast.success('Survey created and residents notified');
      setTitle(''); setDescription(''); setType('poll'); setQuestions([{ question: '', options: ['', ''] }]); setAnonymous(true); setValidDays(7); setExpiresAt(''); setExpiryMode('days');
      onOpenChange(false);
      onCreated && onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="survey-create-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New {type === 'poll' ? 'Poll' : 'Survey'}</DialogTitle><DialogDescription>Get feedback from your residents</DialogDescription></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="survey-type-poll" type="button" onClick={() => { setType('poll'); setQuestions([{ question: '', options: ['', ''] }]); }} className={`p-2 rounded-lg border-2 text-xs ${type === 'poll' ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-semibold' : 'border-slate-200 text-slate-600'}`}>Single-question Poll</button>
            <button data-testid="survey-type-survey" type="button" onClick={() => setType('survey')} className={`p-2 rounded-lg border-2 text-xs ${type === 'survey' ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-semibold' : 'border-slate-200 text-slate-600'}`}>Multi-question Survey</button>
          </div>
          <div><Label>Title</Label><Input data-testid="survey-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Solar panel installation" /></div>
          <div><Label>Description (optional)</Label><Textarea data-testid="survey-desc-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="More context for residents..." /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Anonymous</Label>
              <select data-testid="survey-anon-select" value={anonymous ? 'yes' : 'no'} onChange={(e) => setAnonymous(e.target.value === 'yes')} className="w-full p-2 border rounded-md text-sm">
                <option value="yes">Yes (recommended)</option><option value="no">No (track who voted what)</option>
              </select>
            </div>
            <div><Label>Expiry</Label>
              <select data-testid="survey-expiry-mode" value={expiryMode} onChange={(e) => setExpiryMode(e.target.value)} className="w-full p-2 border rounded-md text-sm">
                <option value="days">Active for X days</option>
                <option value="date">Specific date & time</option>
              </select>
            </div>
          </div>
          {expiryMode === 'days' ? (
            <div><Label>Active for (days)</Label><Input data-testid="survey-days-input" type="number" min={1} max={90} value={validDays} onChange={(e) => setValidDays(e.target.value)} /></div>
          ) : (
            <div><Label>Closes on</Label><Input data-testid="survey-expires-at-input" type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} /><p className="text-[11px] text-slate-500 mt-1">Residents see live results until this time. Max 180 days from now.</p></div>
          )}
          <div className="space-y-3 pt-2 border-t">
            {questions.map((q, qIdx) => (
              <div key={qIdx} className="p-2 bg-slate-50 rounded-lg space-y-2" data-testid={`survey-q-${qIdx}`}>
                <div className="flex gap-2 items-center"><Label className="text-xs">Question {qIdx + 1}</Label>{type === 'survey' && questions.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => removeQ(qIdx)} className="text-red-500 ml-auto h-6 w-6 p-0"><Trash2 className="w-3 h-3" /></Button>}</div>
                <Input data-testid={`survey-q-text-${qIdx}`} value={q.question} onChange={(e) => updateQ(qIdx, 'question', e.target.value)} placeholder="Your question" />
                <div className="space-y-1">
                  {q.options.map((o, oIdx) => (
                    <div key={oIdx} className="flex gap-1">
                      <Input data-testid={`survey-q-${qIdx}-opt-${oIdx}`} value={o} onChange={(e) => updateOpt(qIdx, oIdx, e.target.value)} placeholder={`Option ${oIdx + 1}`} />
                      {q.options.length > 2 && <Button type="button" size="sm" variant="ghost" onClick={() => removeOpt(qIdx, oIdx)} className="text-red-500 h-9 w-9 p-0"><X className="w-3 h-3" /></Button>}
                    </div>
                  ))}
                  {q.options.length < 10 && <Button type="button" size="sm" variant="outline" onClick={() => addOpt(qIdx)} className="text-xs h-7"><Plus className="w-3 h-3 mr-1" />Option</Button>}
                </div>
              </div>
            ))}
            {type === 'survey' && questions.length < 20 && <Button type="button" variant="outline" onClick={addQ} className="w-full"><Plus className="w-4 h-4 mr-1" />Add another question</Button>}
          </div>
          <Button data-testid="survey-create-submit" onClick={submit} disabled={submitting} className="w-full bg-indigo-600 hover:bg-indigo-700">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Create & Notify Residents
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SurveyResultsDialog({ survey, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    surveyAPI.results(survey.id).then(r => setData(r.data)).catch(() => toast.error('Failed to load results')).finally(() => setLoading(false));
  }, [survey.id]);

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="survey-results-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{survey.title}</DialogTitle><DialogDescription>{data ? `${data.response_count} response${data.response_count === 1 ? '' : 's'}` : 'Loading...'}</DialogDescription></DialogHeader>
        {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto py-4" /> : data && (
          <div className="space-y-4 py-2">
            {data.aggregated.map((q, qIdx) => {
              const total = q.counts.reduce((a, b) => a + b, 0) || 1;
              return (
                <div key={qIdx} className="space-y-1">
                  <p className="text-sm font-semibold">{qIdx + 1}. {q.question}</p>
                  {q.options.map((o, oIdx) => {
                    const c = q.counts[oIdx] || 0;
                    const pct = ((c / total) * 100).toFixed(0);
                    return (
                      <div key={oIdx} className="space-y-0.5">
                        <div className="flex justify-between text-xs"><span>{o}</span><span className="font-mono">{c} ({pct}%)</span></div>
                        <div className="w-full bg-slate-200 rounded-full h-2"><div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${pct}%` }} /></div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// --- Chat reports ---
function ChatReportsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await chatAPI.listReports(); setItems(data || []); }
    catch { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    try { await chatAPI.resolveReport(id); toast.success('Resolved'); load(); } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-3">
      {loading ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div> : items.length === 0 ? <p className="text-center py-6 text-sm text-slate-500">No reports — your community is friendly!</p> : (
        <div className="space-y-2">
          {items.map((r) => (
            <div key={r.id} data-testid={`chat-report-${r.id}`} className={`p-3 rounded-lg border ${r.status === 'pending' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200 opacity-70'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="text-sm font-semibold">{r.sender_name}</p>
                    <span className="text-xs text-slate-400">→ reported by {r.reporter_name}</span>
                    <Badge variant="outline" className="text-[10px]">{r.reason}</Badge>
                    <Badge className={`text-[10px] ${r.status === 'pending' ? 'bg-amber-500' : 'bg-emerald-500'}`}>{r.status}</Badge>
                  </div>
                  {r.message_body && <p className="text-xs italic text-slate-700 mt-1 px-2 py-1 bg-white rounded border border-slate-200">"{r.message_body}"</p>}
                  {r.notes && <p className="text-xs text-slate-500 mt-1">Notes: {r.notes}</p>}
                  <p className="text-[10px] text-slate-400 mt-1">{new Date(r.created_at).toLocaleString()}</p>
                </div>
                {r.status === 'pending' && <Button data-testid={`resolve-report-${r.id}`} size="sm" variant="ghost" onClick={() => resolve(r.id)} className="text-emerald-600"><Check className="w-4 h-4" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// --- Monthly Digest ---
function MonthlyDigestAdmin() {
  const [items, setItems] = useState([]);
  const [recapItems, setRecapItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendingRecaps, setSendingRecaps] = useState(false);
  const [sendingPulse, setSendingPulse] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [logRes, recapRes] = await Promise.all([reportsAPI.log(), reportsAPI.recapLog()]);
      setItems(logRes.data || []);
      setRecapItems(recapRes.data || []);
    } catch (e) { console.error('Failed to load digest log', e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendNow = async (force = false) => {
    setSending(true);
    try {
      const { data } = await reportsAPI.triggerMonthlyDigest(force);
      if (data.sent) {
        toast.success(`Digest sent to ${data.recipients.length} recipient(s)`);
      } else if (data.reason === 'already_sent') {
        if (window.confirm(`Already sent for ${data.period}. Send again anyway?`)) {
          await reportsAPI.triggerMonthlyDigest(true).then(r => r.data.sent && toast.success('Digest re-sent'));
        }
      } else {
        toast.error(data.reason || 'Could not send');
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };

  const sendRecapsNow = async (force = false) => {
    setSendingRecaps(true);
    try {
      const { data } = await reportsAPI.triggerResidentRecaps(force);
      const total = (data.sent || 0) + (data.skipped_already_sent || 0);
      if (data.sent > 0) {
        toast.success(`Recaps sent to ${data.sent} resident(s) for ${data.period}` +
          (data.skipped_already_sent ? ` (${data.skipped_already_sent} already sent)` : ''));
      } else if (data.skipped_already_sent > 0) {
        if (window.confirm(`All ${data.skipped_already_sent} residents already received the ${data.period} recap. Re-send anyway?`)) {
          const r = await reportsAPI.triggerResidentRecaps(true);
          toast.success(`Recaps re-sent to ${r.data.sent} resident(s)`);
        }
      } else if (total === 0) {
        toast.error('No residents with email addresses found');
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSendingRecaps(false); }
  };

  const sendPulseNow = async (force = false) => {
    setSendingPulse(true);
    try {
      const { data } = await reportsAPI.triggerWeeklyPulse(force);
      if (data.reason === 'already_sent') {
        if (window.confirm(`Pulse already sent for week ${data.week}. Send again anyway?`)) {
          const r = await reportsAPI.triggerWeeklyPulse(true);
          toast.success(`Weekly pulse re-sent · ${r.data.sent_management} mgmt + ${r.data.sent_residents} residents`);
        }
      } else {
        toast.success(`Weekly pulse sent · ${data.sent_management} mgmt + ${data.sent_residents} residents notified`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSendingPulse(false); }
  };

  return (
    <div className="space-y-3">
      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-emerald-600 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-emerald-800">Monthly Management Digest</p>
            <p className="text-xs text-emerald-700 mt-0.5">Auto-sends on the 1st of every month to all management emails. Includes visitor traffic, vehicle activity, blacklist hits, community engagement and top-rated recommendations.</p>
            <Button data-testid="send-digest-now-btn" onClick={() => sendNow(false)} disabled={sending} size="sm" className="mt-2 bg-emerald-600 hover:bg-emerald-700">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
              Send Last Month's Digest Now
            </Button>
          </div>
        </div>
      </div>

      <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-teal-600 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-teal-800">Resident "My Month" Recap</p>
            <p className="text-xs text-teal-700 mt-0.5">Personal email to each resident with their gate passes, deliveries received, visitors at their villa, last month's bookings, and upcoming bookings (next 30 days). Auto-sends on the 1st alongside the management digest.</p>
            <Button data-testid="send-recaps-now-btn" onClick={() => sendRecapsNow(false)} disabled={sendingRecaps} size="sm" className="mt-2 bg-teal-600 hover:bg-teal-700">
              {sendingRecaps ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
              Send Resident Recaps Now
            </Button>
          </div>
        </div>
      </div>

      <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
        <div className="flex items-start gap-3">
          <BarChart3 className="w-5 h-5 text-cyan-600 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-cyan-800">Weekly Community Pulse</p>
            <p className="text-xs text-cyan-700 mt-0.5">Single in-app notification every Sunday. Management sees community-wide stats; residents see their personal gate-pass / delivery / visitor counts plus community-wide new recommendations and new residents.</p>
            <Button data-testid="send-pulse-now-btn" onClick={() => sendPulseNow(false)} disabled={sendingPulse} size="sm" className="mt-2 bg-cyan-600 hover:bg-cyan-700">
              {sendingPulse ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-1" />}
              Send Weekly Pulse Now
            </Button>
          </div>
        </div>
      </div>

      <p className="text-xs font-semibold text-slate-600 mt-2">Recent management digests:</p>
      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto py-3" /> : items.length === 0 ? <p className="text-center text-sm text-slate-500 py-3">No digests sent yet.</p> : (
        <div className="space-y-1.5">
          {items.map((r) => (
            <div key={r.id} data-testid={`digest-log-${r.period}`} className="p-2.5 bg-slate-50 rounded border border-slate-200">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs font-semibold">{r.period}</div>
                <div className="text-[11px] text-slate-500">Sent: {new Date(r.sent_at).toLocaleString()}</div>
              </div>
              <div className="text-[11px] text-slate-600 mt-1">
                {r.sent_to.length} recipient(s) ·
                Visitors {r.stats_summary.visitors_total ?? 0} ·
                ANPR {r.stats_summary.anpr_total ?? 0} ·
                Blacklist hits {r.stats_summary.blacklist_hits ?? 0} ·
                New residents {r.stats_summary.new_residents ?? 0}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs font-semibold text-slate-600 mt-3">Resident recap batches:</p>
      {loading ? null : recapItems.length === 0 ? <p className="text-center text-sm text-slate-500 py-3">No recap batches sent yet.</p> : (
        <div className="space-y-1.5">
          {recapItems.map((r) => (
            <div key={`recap-${r.period}`} data-testid={`recap-log-${r.period}`} className="p-2.5 bg-teal-50 rounded border border-teal-200">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs font-semibold text-teal-800">{r.period}</div>
                <div className="text-[11px] text-teal-700">Last sent: {new Date(r.last_sent_at).toLocaleString()}</div>
              </div>
              <div className="text-[11px] text-teal-700 mt-1">{r.recipient_count} resident(s) emailed</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
